/**
 * Dispute Command Center — premium lifecycle dashboard at /disputes
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  Plus,
  Download,
  XCircle,
  FileText,
  Loader2,
  Eye,
  Sparkles,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Mail,
  Printer,
  Trash2,
  ChevronDown,
  MoreHorizontal,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSupportContext } from '../../context/SupportContext';
import { useI18n } from '../../context/I18nContext';
import { metro2Service } from '../../services/metro2Service';
import LetterPreviewModal from '../../components/disputes/LetterPreviewModal';
import ChooseLetterPreviewModal from '../../components/disputes/ChooseLetterPreviewModal';
import { toast } from 'sonner';
import axios from 'axios';
import {
  cachedFetch,
  fetchCacheKey,
  invalidateFetchCache,
} from '../../utils/fetchCache';
import {
  LIFECYCLE_TABS,
  normalizeGeneratedLetter,
  collectRoundLetterSources,
  logDisputeCenterLetterAudit,
  resolveDisputeCenterUserId,
  pickDefaultLetter,
  getProgressSteps,
  filterLettersByTab,
  searchLetters,
  sortLetters,
  paginateLetters,
  computeSummaryStats,
  buildOutcomeMatrix,
  buildLatestActivity,
  computeNextBestAction,
  formatLetterDate,
  getPreviewCandidatesForLetter,
  resolveLetterPreviewPacket,
  isLetterDeletable,
} from './disputeCenterUtils';
import './DisputeCenter.css';

const DC = 'disputeCenter';
const API = `${(process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '')}/api`;
const DISPUTE_CENTER_CACHE_TTL_MS = 20000;

const PER_PAGE_OPTIONS = [10, 25, 50];

function statusClass(status) {
  return `dc-status-badge dc-status-${status}`;
}

function TruncateCell({ value, className = '' }) {
  const text = value || '—';
  return (
    <span className={`dc-truncate ${className}`.trim()} title={text}>
      {text}
    </span>
  );
}

function SortableHeader({ label, field, columnSort, onToggle }) {
  const active = columnSort?.field === field;
  const Icon = active ? (columnSort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      type="button"
      className={`dc-th-sort${active ? ' active' : ''}`}
      onClick={() => onToggle(field)}
    >
      {label}
      <Icon size={13} aria-hidden />
    </button>
  );
}

const DisputeCenterDashboard = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, token, getAuthHeaders } = useAuth();
  const { supportContext, isInSupportMode, contextReady } = useSupportContext();

  const [loading, setLoading] = useState(true);
  const [letters, setLetters] = useState([]);
  const [lifecycleTab, setLifecycleTab] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('newest');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [columnSort, setColumnSort] = useState(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLetter, setPreviewLetter] = useState(null);
  const [resolvedAttachments, setResolvedAttachments] = useState([]);
  const [pdfApiAttachments, setPdfApiAttachments] = useState([]);
  const [choosePreviewOpen, setChoosePreviewOpen] = useState(false);
  const [choosePreviewLetters, setChoosePreviewLetters] = useState([]);
  const [choosePreviewDefaultId, setChoosePreviewDefaultId] = useState(null);
  const [postgridPreviewOpen, setPostgridPreviewOpen] = useState(false);
  const [postgridPreviewLoading, setPostgridPreviewLoading] = useState(false);
  const [postgridPreviewUrl, setPostgridPreviewUrl] = useState(null);
  const postgridPreviewUrlRef = useRef(null);
  const [letterContent, setLetterContent] = useState(null);
  const [letterLoading, setLetterLoading] = useState(false);

  const statusLabel = useCallback(
    (status) => {
      const key = `${DC}.statuses.${status}`;
      const label = t(key);
      return label !== key ? label : status;
    },
    [t]
  );

  const disputeCenterUser = useMemo(
    () => resolveDisputeCenterUserId(user, supportContext, isInSupportMode),
    [user, supportContext, isInSupportMode]
  );

  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const [syncing, setSyncing] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const loadLetters = useCallback(async ({ force = false, syncPostGrid = false } = {}) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setLoadError(null);
    try {
      if (isInSupportMode && !contextReady) {
        return;
      }

      const userId = disputeCenterUser.userId;
      if (!userId) {
        if (mountedRef.current) {
          setLetters([]);
        }
        return;
      }

      if (syncPostGrid && token) {
        setSyncing(true);
        try {
          await axios.post(
            `${API}/delivery/mail/sync-status?force=true`,
            {},
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 30000,
            }
          );
        } catch (syncErr) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[DisputeCenter] PostGrid sync-status failed', syncErr);
          }
          toast.warning(t(`${DC}.syncFailed`, { defaultValue: 'PostGrid sync failed; showing local data.' }));
        } finally {
          if (mountedRef.current) {
            setSyncing(false);
          }
        }
      }

      const cacheKey = fetchCacheKey('dispute-center', userId, disputeCenterUser.source);
      if (force || syncPostGrid) {
        invalidateFetchCache(cacheKey);
      }

      const readModel = await cachedFetch(
        cacheKey,
        (signal) =>
          metro2Service.getDisputeCenterReadModel(userId, token, {
            limit: 100,
            signal: signal || controller.signal,
          }),
        {
          ttlMs: DISPUTE_CENTER_CACHE_TTL_MS,
          signal: controller.signal,
          force: force || syncPostGrid,
        }
      );

      if (!mountedRef.current || controller.signal.aborted) {
        return;
      }

      const roundDetails = readModel.items || [];

      const normalized = [];
      const roundAudits = [];
      roundDetails.forEach(({ round, packages, generatedLetters, generated_letters: genAlt }) => {
        const letters = generatedLetters || genAlt || [];
        const { rows, audit } = collectRoundLetterSources(round, packages, letters);
        roundAudits.push(audit);
        rows.forEach(({ pkg }) => {
          normalized.push(normalizeGeneratedLetter(pkg, round));
        });
      });

      logDisputeCenterLetterAudit(roundAudits, normalized.length, lifecycleTab);

      setLetters(normalized);
      const def = pickDefaultLetter(normalized);
      setSelectedId(def?.id ?? null);
      setPage(1);
    } catch (error) {
      if (controller.signal.aborted || error?.name === 'AbortError') {
        return;
      }
      console.error('Failed to load dispute letters:', error);
      if (mountedRef.current) {
        setLoadError(error.message || 'Failed to load');
        toast.error(t(`${DC}.loadFailed`));
        setLetters([]);
        setSelectedId(null);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [token, t, disputeCenterUser, isInSupportMode, contextReady]);

  const handleRefresh = useCallback(async () => {
    await loadLetters({ force: true, syncPostGrid: true });
  }, [loadLetters]);

  useEffect(() => {
    loadLetters();
  }, [loadLetters]);

  const tabLetters = useMemo(
    () => filterLettersByTab(letters, lifecycleTab),
    [letters, lifecycleTab]
  );

  const filteredLetters = useMemo(() => {
    const searched = searchLetters(tabLetters, searchQuery);
    return sortLetters(searched, sortKey, columnSort);
  }, [tabLetters, searchQuery, sortKey, columnSort]);

  const selectedLetters = useMemo(
    () => letters.filter((l) => selectedIds.has(l.id)),
    [letters, selectedIds]
  );

  const selectedCount = selectedIds.size;

  const pagination = useMemo(
    () => paginateLetters(filteredLetters, page, perPage),
    [filteredLetters, page, perPage]
  );

  useEffect(() => {
    setPage(1);
  }, [lifecycleTab, searchQuery, sortKey, perPage, columnSort]);

  useEffect(() => {
    setMoreMenuOpen(false);
  }, [lifecycleTab, searchQuery, page, perPage]);

  useEffect(() => {
    if (!selectedId) return;
    const visible = filteredLetters.some((l) => l.id === selectedId);
    if (!visible && filteredLetters.length) {
      setSelectedId(pickDefaultLetter(filteredLetters)?.id ?? null);
    } else if (!visible) {
      setSelectedId(null);
    }
  }, [filteredLetters, selectedId]);

  const selectedLetter = useMemo(
    () => letters.find((l) => l.id === selectedId) || null,
    [letters, selectedId]
  );

  const summary = useMemo(() => computeSummaryStats(letters), [letters]);
  const nextAction = useMemo(() => computeNextBestAction(letters, t, DC), [letters, t]);
  const outcomeMatrix = useMemo(() => buildOutcomeMatrix(letters), [letters]);
  const latestEvents = useMemo(() => buildLatestActivity(letters), [letters]);
  const progress = useMemo(() => getProgressSteps(selectedLetter), [selectedLetter]);

  const loadLetterContent = async (letter, openModal = false) => {
    if (!letter?.id) {
      if (openModal) {
        toast.error(t(`${DC}.preview.unavailable`));
      }
      return;
    }
    const pkg = letter.rawPackage;
    setPreviewLetter(letter);
    setLetterLoading(true);

    if (!pkg?.letter_url) {
      setLetterContent(null);
      if (openModal) setPreviewOpen(true);
      setLetterLoading(false);
      return;
    }

    try {
      if (pkg.letter_url.startsWith('data:text/html;base64,')) {
        const base64Content = pkg.letter_url.replace('data:text/html;base64,', '');
        const htmlContent = atob(base64Content);
        setLetterContent({
          success: true,
          package_id: pkg.id,
          round_id: letter.roundId,
          content: htmlContent,
        });
        if (openModal) setPreviewOpen(true);
        return;
      }

      if (!letter.roundId) {
        throw new Error('Missing dispute round for this letter');
      }
      const packageId = pkg._linkedPackageId || pkg.id;
      const result = await metro2Service.getLetter(letter.roundId, packageId, token);
      setLetterContent(result);
      if (openModal) setPreviewOpen(true);
    } catch (error) {
      console.error('Failed to load letter:', error);
      const msg = error.message || '';
      if (!msg.includes('not yet generated') && !msg.includes('not generated')) {
        toast.error(t(`${DC}.preview.unavailable`));
      }
      setLetterContent(null);
      if (openModal) setPreviewOpen(true);
    } finally {
      setLetterLoading(false);
    }
  };

  const openLetterPreview = async (letter) => {
    const packet = resolveLetterPreviewPacket(letter);
    setResolvedAttachments(packet.resolvedAttachments);
    setPdfApiAttachments(packet.pdfApiAttachments);
    await loadLetterContent(letter, true);
  };

  const cleanupPostgridPreviewUrl = useCallback(() => {
    if (postgridPreviewUrlRef.current) {
      window.URL.revokeObjectURL(postgridPreviewUrlRef.current);
      postgridPreviewUrlRef.current = null;
    }
    setPostgridPreviewUrl(null);
  }, []);

  useEffect(() => () => cleanupPostgridPreviewUrl(), [cleanupPostgridPreviewUrl]);

  const handlePostGridPreview = async () => {
    if (!previewLetter?.id) {
      toast.error(t(`${DC}.preview.unavailable`));
      return;
    }

    const packet = resolveLetterPreviewPacket(previewLetter);
    setResolvedAttachments(packet.resolvedAttachments);
    setPdfApiAttachments(packet.pdfApiAttachments);

    setPostgridPreviewOpen(true);
    setPostgridPreviewLoading(true);
    cleanupPostgridPreviewUrl();

    try {
      const response = await axios.post(
        `${API}/delivery/pdf/generate-postgrid-preview`,
        {
          letter_id: previewLetter.rawPackage?._letterId || previewLetter.id,
          package_id: previewLetter.rawPackage?._linkedPackageId || previewLetter.rawPackage?.id,
          include_attachments: true,
          envelope_type: packet.isDataBreach ? 'data_breach' : 'regular_dispute',
          attachments: packet.pdfApiAttachments,
        },
        {
          headers: getAuthHeaders(),
          responseType: 'blob',
          withCredentials: true,
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      postgridPreviewUrlRef.current = url;
      setPostgridPreviewUrl(url);
    } catch (error) {
      console.error('PostGrid preview failed:', error);
      toast.error(t(`${DC}.preview.postgridFailed`));
      setPostgridPreviewOpen(false);
    } finally {
      setPostgridPreviewLoading(false);
    }
  };

  const handleFutureSendLetter = () => {
    toast.info(t(`${DC}.preview.sendComingSoon`));
  };

  const previewCanSend =
    previewLetter?.lifecycleStatus === 'ready' && !['cancelled', 'failed'].includes(previewLetter?.lifecycleStatus);

  const handlePreview = (e, letter) => {
    e.stopPropagation();
    const candidates = getPreviewCandidatesForLetter(letters, letter);
    if (candidates.length > 1) {
      setChoosePreviewLetters(candidates);
      setChoosePreviewDefaultId(letter.id);
      setChoosePreviewOpen(true);
      return;
    }
    openLetterPreview(letter);
  };

  const handleChoosePreviewLetter = (letter) => {
    setChoosePreviewOpen(false);
    setChoosePreviewLetters([]);
    setChoosePreviewDefaultId(null);
    openLetterPreview(letter);
  };

  const handleDownloadLetter = () => {
    if (!letterContent?.content || !previewLetter) {
      toast.error(t(`${DC}.preview.unavailable`));
      return;
    }
    const blob = new Blob([letterContent.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispute_letter_${previewLetter.bureau || 'package'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadLetterFile = async (letter) => {
    const pkg = letter.rawPackage;
    if (!pkg?.letter_url) return false;
    let content = '';
    if (pkg.letter_url.startsWith('data:text/html;base64,')) {
      content = atob(pkg.letter_url.replace('data:text/html;base64,', ''));
    } else {
      const result = await metro2Service.getLetter(letter.roundId, letter.id, token);
      content = result?.content || '';
    }
    if (!content) return false;
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispute_${letter.bureau}_${String(letter.id).slice(0, 8)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  };

  const toggleColumnSort = (field) => {
    setColumnSort((prev) => {
      if (prev?.field !== field) return { field, dir: 'desc' };
      if (prev.dir === 'desc') return { field, dir: 'asc' };
      return null;
    });
  };

  const toggleRowSelected = (e, letterId) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(letterId)) next.delete(letterId);
      else next.add(letterId);
      return next;
    });
  };

  const visibleIds = useMemo(() => pagination.items.map((l) => l.id), [pagination.items]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleSelectAllVisible = (e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleSelectFullPackage = () => {
    const groupKeys = new Set(selectedLetters.map((l) => l.packageGroupKey));
    if (!groupKeys.size) return;
    const relatedIds = filteredLetters
      .filter((l) => groupKeys.has(l.packageGroupKey))
      .map((l) => l.id);
    setSelectedIds(new Set(relatedIds));
  };

  const handleBulkDownload = async (targets = null) => {
    const pool = targets || selectedLetters;
    const downloadable = pool.filter((l) => l.letterUrl);
    if (!downloadable.length) {
      toast.error(t(`${DC}.letters.downloadNone`));
      return;
    }
    toast.info(t(`${DC}.letters.downloadStarted`));
    let count = 0;
    for (const letter of downloadable.slice(0, 20)) {
      try {
        if (await downloadLetterFile(letter)) count += 1;
      } catch {
        /* continue */
      }
    }
    if (!count) toast.error(t(`${DC}.letters.downloadNone`));
  };

  const handleBulkSendEmail = () => {
    toast.info(t(`${DC}.letters.bulk.sendEmailSoon`));
  };

  const handleBulkSendFax = () => {
    toast.info(t(`${DC}.letters.bulk.sendFaxSoon`));
  };

  const handleBulkDelete = async () => {
    if (!selectedLetters.length || deleting) return;

    const deletable = selectedLetters.filter(isLetterDeletable);
    if (!deletable.length) {
      toast.error(t(`${DC}.letters.bulk.deleteFailed`));
      return;
    }

    const confirmMessage = t(`${DC}.letters.bulk.deleteConfirm`, {
      count: deletable.length,
    });
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const userId = disputeCenterUser.userId;
    if (!userId || !token) {
      toast.error(t(`${DC}.letters.bulk.deleteFailed`));
      return;
    }

    setDeleting(true);
    try {
      const result = await metro2Service.bulkDeleteDisputeLetters(
        userId,
        deletable.map((letter) => letter.id),
        token
      );

      const deletedCount = result?.deleted_count ?? 0;
      if (deletedCount > 0) {
        toast.success(
          t(`${DC}.letters.bulk.deleteSuccess`, { count: deletedCount })
        );
      }

      if ((result?.skipped?.length ?? 0) > 0 || (result?.not_found?.length ?? 0) > 0) {
        toast.warning(t(`${DC}.letters.bulk.deletePartial`));
      }

      if (!deletedCount && !(result?.skipped?.length)) {
        toast.error(t(`${DC}.letters.bulk.deleteFailed`));
      }

      setSelectedIds(new Set());
      await loadLetters({ force: true });
    } catch (error) {
      console.error('Failed to delete dispute letters:', error);
      toast.error(error.message || t(`${DC}.letters.bulk.deleteFailed`));
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelLetters = () => {
    toast.info(t(`${DC}.letters.cancelNotAvailable`));
  };

  const handleNewDispute = () => navigate('/dispute-wizard');

  const clearFilters = () => {
    setSearchQuery('');
    setSortKey('newest');
    setColumnSort(null);
    setLifecycleTab('all');
    setPage(1);
    setSelectedIds(new Set());
  };

  if (loading) {
    return (
      <div className="dispute-center flex items-center justify-center py-24" data-testid="dispute-center-loading">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        <span className="ml-3 text-stone-600 dark:text-stone-300">{t(`${DC}.loading`)}</span>
      </div>
    );
  }

  return (
    <div className="dispute-center" data-testid="dispute-center-dashboard">
      <div className="dc-shell">
        {/* 1. Page header */}
        <section className="dc-card">
          <div className="dc-card-inner">
            <div className="dc-topbar">
              <div>
                <div className="dc-kicker">
                  <span className="dc-dot" />
                  {t(`${DC}.lifecycle.title`)}
                </div>
                <h1 className="dc-page-title">{t(`${DC}.header.title`)}</h1>
                <p className="dc-page-subtitle">{t(`${DC}.header.subtitle`)}</p>
              </div>
              <div className="dc-actions">
                <button type="button" className="dc-btn dc-btn-outline" onClick={handleRefresh} disabled={loading || syncing}>
                  <RefreshCw size={16} className={loading || syncing ? 'animate-spin' : ''} />
                  {t(`${DC}.actions.refresh`)}
                </button>
                <button type="button" className="dc-btn dc-btn-primary" onClick={handleNewDispute} data-testid="new-dispute-btn">
                  <Plus size={16} />
                  {t(`${DC}.actions.newDispute`)}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Next Best Action */}
        <section className="dc-card">
          <div className="dc-card-inner dc-next-card">
            <div>
              <div className="dc-kicker">
                <Sparkles size={14} />
                {t(`${DC}.nextBestAction.title`)}
              </div>
              <h2 className="dc-next-title">{t(`${DC}.nextBestAction.${nextAction.titleKey}`)}</h2>
              <p className="dc-next-copy">{t(`${DC}.nextBestAction.${nextAction.bodyKey}`)}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {(nextAction.pills || []).map((pill) => (
                  <span key={pill} className="dc-pill emerald">
                    {t(`${DC}.nextBestAction.pills.${pill}`)}
                  </span>
                ))}
              </div>
            </div>
            {letters.some((l) => l.lifecycleStatus === 'ready') && (
              <button
                type="button"
                className="dc-btn dc-btn-primary"
                onClick={() => {
                  setLifecycleTab('ready');
                  const ready = pickDefaultLetter(filterLettersByTab(letters, 'ready'));
                  if (ready) setSelectedId(ready.id);
                }}
              >
                {t(`${DC}.nextBestAction.reviewLetters`)}
              </button>
            )}
          </div>
        </section>

        {/* 3. Summary cards */}
        <div className="dc-stats">
          <div className="dc-stat dc-card">
            <div className="dc-stat-value">{summary.all || 0}</div>
            <div className="dc-stat-label">{t(`${DC}.summary.total`)}</div>
          </div>
          <div className="dc-stat dc-card">
            <div className="dc-stat-value">{summary.ready || 0}</div>
            <div className="dc-stat-label">{t(`${DC}.summary.ready`)}</div>
          </div>
          <div className="dc-stat dc-card">
            <div className="dc-stat-value">
              {(summary.printing || 0) + (summary.processed || 0) + (summary.sent || 0)}
            </div>
            <div className="dc-stat-label">{t(`${DC}.summary.inTransit`)}</div>
          </div>
          <div className="dc-stat dc-card">
            <div className="dc-stat-value">{summary.completed || 0}</div>
            <div className="dc-stat-label">{t(`${DC}.summary.completed`)}</div>
          </div>
          <div className="dc-stat dc-card">
            <div className="dc-stat-value">{(summary.failed || 0) + (summary.cancelled || 0)}</div>
            <div className="dc-stat-label">{t(`${DC}.summary.issues`)}</div>
          </div>
        </div>

        {/* 4–7. Lifecycle + Letter Details + Generated Letters */}
        <section className="dc-card">
          <div className="dc-card-inner">
            <div className="dc-section-head">
              <div>
                <h2 className="dc-section-title">{t(`${DC}.lifecycle.title`)}</h2>
                <p className="dc-section-copy">{t(`${DC}.lifecycle.subtitle`)}</p>
              </div>
              <span className="dc-pill emerald">{t(`${DC}.lifecycle.autoSync`)}</span>
            </div>

            <div className="dc-lifecycle-tabs" role="tablist">
              {LIFECYCLE_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={lifecycleTab === tab}
                  className={`dc-lifecycle-tab${lifecycleTab === tab ? ' active' : ''}`}
                  onClick={() => setLifecycleTab(tab)}
                >
                  {t(`${DC}.lifecycle.tabs.${tab}`)}
                  {tab !== 'all' && (
                    <span style={{ marginLeft: 6, opacity: 0.7 }}>
                      ({tab === 'draft'
                        ? (summary.draft || 0)
                        : summary[tab] ?? 0})
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* 5. Letter Details — full width */}
            <div className="dc-letter-details dc-card" style={{ boxShadow: 'none', marginBottom: 20 }}>
              <div className="dc-card-inner" style={{ padding: '18px 20px' }}>
                <div className="dc-detail-header">
                  <div>
                    <h3 className="dc-detail-title">{t(`${DC}.letterDetails.title`)}</h3>
                    <p className="dc-detail-subtitle">
                      {selectedLetter
                        ? t(`${DC}.letterDetails.subtitleSelected`)
                        : t(`${DC}.letterDetails.subtitle`)}
                    </p>
                    {selectedLetter && (
                      <div className="dc-latest-activity">
                        {t(`${DC}.letterDetails.latestActivity`, {
                          text: t(`${DC}.activity.${selectedLetter.latestActivityKey}`),
                        })}
                      </div>
                    )}
                  </div>
                  {selectedLetter && (
                    <span className={statusClass(selectedLetter.lifecycleStatus)}>
                      <span className="dc-status-dot" />
                      {statusLabel(selectedLetter.lifecycleStatus)}
                    </span>
                  )}
                </div>

                {selectedLetter ? (
                  <>
                    <p style={{ margin: '0 0 14px', fontWeight: 700, color: 'var(--dc-heading)' }}>
                      {selectedLetter.description}
                    </p>
                    <div className="dc-meta-grid">
                      <div className="dc-meta">
                        <div className="dc-meta-label">{t(`${DC}.letterDetails.round`)}</div>
                        <div className="dc-meta-value">{selectedLetter.roundLabel}</div>
                      </div>
                      <div className="dc-meta">
                        <div className="dc-meta-label">{t(`${DC}.letterDetails.method`)}</div>
                        <div className="dc-meta-value">{selectedLetter.method}</div>
                      </div>
                      <div className="dc-meta">
                        <div className="dc-meta-label">{t(`${DC}.letterDetails.letterType`)}</div>
                        <div className="dc-meta-value">{selectedLetter.letterType}</div>
                      </div>
                      <div className="dc-meta">
                        <div className="dc-meta-label">{t(`${DC}.letterDetails.bureau`)}</div>
                        <div className="dc-meta-value">{selectedLetter.bureau}</div>
                      </div>
                      <div className="dc-meta">
                        <div className="dc-meta-label">{t(`${DC}.letterDetails.recipient`)}</div>
                        <div className="dc-meta-value">{selectedLetter.recipient}</div>
                      </div>
                      <div className="dc-meta">
                        <div className="dc-meta-label">{t(`${DC}.letterDetails.created`)}</div>
                        <div className="dc-meta-value">{formatLetterDate(selectedLetter.createdAt)}</div>
                      </div>
                      <div className="dc-meta">
                        <div className="dc-meta-label">{t(`${DC}.letterDetails.lastUpdated`)}</div>
                        <div className="dc-meta-value">{formatLetterDate(selectedLetter.updatedAt)}</div>
                      </div>
                      <div className="dc-meta">
                        <div className="dc-meta-label">{t(`${DC}.letterDetails.itemsIncluded`)}</div>
                        <div className="dc-meta-value">{selectedLetter.itemsSummary}</div>
                      </div>
                      <div className="dc-meta">
                        <div className="dc-meta-label">{t(`${DC}.letterDetails.nextAction`)}</div>
                        <div className="dc-meta-value">
                          {t(`${DC}.nextActions.${selectedLetter.nextActionKey}`)}
                        </div>
                      </div>
                    </div>

                    <div className="dc-progress-head">
                      <h4 className="dc-progress-title">{t(`${DC}.letterDetails.progressTitle`)}</h4>
                      <p className="dc-progress-copy">{t(`${DC}.letterDetails.progressSubtitle`)}</p>
                    </div>
                    <div className="dc-timeline-progress">
                      {progress.steps.map((step) => {
                        let cls = 'dc-timeline-step';
                        if (step.state === 'complete') cls += ' complete';
                        if (step.state === 'current') cls += ' current';
                        if (step.state === 'terminal' && progress.variant === 'cancelled') {
                          cls += ' terminal-cancelled';
                        }
                        if (step.state === 'terminal' && progress.variant === 'failed') {
                          cls += ' terminal-failed';
                        }
                        return (
                          <div key={step.key} className={cls}>
                            {t(`${DC}.progress.${step.key}`)}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="dc-section-copy">{t(`${DC}.letterDetails.noSelection`)}</p>
                )}
              </div>
            </div>

            {/* 6–8. Generated Letters */}
            <div className="dc-generated-letters">
              <div className="dc-letters-header">
                <div>
                  <h3 className="dc-section-title">{t(`${DC}.letters.title`)}</h3>
                  <p className="dc-section-copy">{t(`${DC}.letters.subtitle`)}</p>
                </div>
                <div className="dc-actions">
                  <button
                    type="button"
                    className="dc-btn dc-btn-outline dc-btn-small"
                    onClick={() => handleBulkDownload(filteredLetters.filter((l) => l.letterUrl))}
                  >
                    <Download size={14} />
                    {t(`${DC}.letters.downloadLetters`)}
                  </button>
                  <button type="button" className="dc-btn dc-btn-danger dc-btn-small" onClick={handleCancelLetters}>
                    <XCircle size={14} />
                    {t(`${DC}.letters.cancelLetters`)}
                  </button>
                  <button type="button" className="dc-btn dc-btn-primary dc-btn-small" onClick={handleNewDispute}>
                    <FileText size={14} />
                    {t(`${DC}.letters.createLetter`)}
                  </button>
                </div>
              </div>

              <div className="dc-toolbar">
                <input
                  type="search"
                  className="dc-search"
                  placeholder={t(`${DC}.letters.searchPlaceholder`)}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label={t(`${DC}.letters.searchPlaceholder`)}
                />
                <select
                  className="dc-select"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  aria-label={t(`${DC}.letters.sort.newest`)}
                >
                  <option value="newest">{t(`${DC}.letters.sort.newest`)}</option>
                  <option value="oldest">{t(`${DC}.letters.sort.oldest`)}</option>
                  <option value="status">{t(`${DC}.letters.sort.status`)}</option>
                  <option value="bureau">{t(`${DC}.letters.sort.bureau`)}</option>
                </select>
                <button type="button" className="dc-btn dc-btn-outline dc-btn-small" onClick={clearFilters}>
                  {t(`${DC}.letters.clear`)}
                </button>
              </div>

              <div className={`dc-bulk-bar${selectedCount ? '' : ' is-empty'}`}>
                <div className="dc-bulk-left">
                  <span className="dc-bulk-count">
                    {selectedCount
                      ? t(`${DC}.letters.bulk.selected`, { count: selectedCount })
                      : t(`${DC}.letters.bulk.noneSelected`)}
                  </span>
                  {selectedCount > 0 && (
                    <button
                      type="button"
                      className="dc-btn dc-btn-outline dc-btn-small"
                      onClick={handleSelectFullPackage}
                    >
                      {t(`${DC}.letters.bulk.selectFullPackage`)}
                    </button>
                  )}
                </div>
                {selectedCount > 0 && (
                  <div className="dc-bulk-actions">
                    <button
                      type="button"
                      className="dc-btn dc-btn-outline dc-btn-small"
                      onClick={() => handleBulkDownload()}
                    >
                      <Download size={14} />
                      {t(`${DC}.letters.bulk.download`)}
                    </button>
                    <button
                      type="button"
                      className="dc-btn dc-btn-outline dc-btn-small"
                      onClick={handleBulkSendEmail}
                    >
                      <Mail size={14} />
                      {t(`${DC}.letters.bulk.sendEmail`)}
                    </button>
                    <button
                      type="button"
                      className="dc-btn dc-btn-outline dc-btn-small"
                      onClick={handleBulkSendFax}
                    >
                      <Printer size={14} />
                      {t(`${DC}.letters.bulk.sendFax`)}
                    </button>
                    <button
                      type="button"
                      className="dc-btn dc-btn-danger dc-btn-small"
                      onClick={handleBulkDelete}
                      disabled={deleting}
                    >
                      <Trash2 size={14} />
                      {deleting
                        ? t(`${DC}.letters.bulk.deleting`)
                        : t(`${DC}.letters.bulk.delete`)}
                    </button>
                    <div className="dc-more-wrap">
                      <button
                        type="button"
                        className="dc-btn dc-btn-outline dc-btn-small"
                        onClick={() => setMoreMenuOpen((o) => !o)}
                        aria-expanded={moreMenuOpen}
                      >
                        <MoreHorizontal size={14} />
                        {t(`${DC}.letters.bulk.more`)}
                        <ChevronDown size={12} />
                      </button>
                      {moreMenuOpen && (
                        <div className="dc-more-menu" role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setMoreMenuOpen(false);
                              toast.info(t(`${DC}.letters.bulk.exportCsvSoon`));
                            }}
                          >
                            Export CSV
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {pagination.items.length === 0 ? (
                <div className="dc-empty">
                  <h3>{t(`${DC}.letters.emptyTitle`)}</h3>
                  <p>{t(`${DC}.letters.emptyBody`)}</p>
                  <button type="button" className="dc-btn dc-btn-primary" onClick={handleNewDispute}>
                    <Plus size={16} />
                    {t(`${DC}.letters.createLetter`)}
                  </button>
                </div>
              ) : (
                <>
                  <div className="dc-table-wrap">
                    <table className="dc-table">
                      <thead>
                        <tr>
                          <th className="dc-table-col-check">
                            <input
                              type="checkbox"
                              className="dc-checkbox"
                              checked={allVisibleSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                              }}
                              onChange={toggleSelectAllVisible}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={t(`${DC}.letters.bulk.selectFullPackage`)}
                            />
                          </th>
                          <th>{t(`${DC}.letters.columns.description`)}</th>
                          <th>{t(`${DC}.letters.columns.bureau`)}</th>
                          <th>{t(`${DC}.letters.columns.recipient`)}</th>
                          <th>{t(`${DC}.letters.columns.address`)}</th>
                          <th>{t(`${DC}.letters.columns.status`)}</th>
                          <th>
                            <SortableHeader
                              label={t(`${DC}.letters.columns.createdAt`)}
                              field="createdAt"
                              columnSort={columnSort}
                              onToggle={toggleColumnSort}
                            />
                          </th>
                          <th>
                            <SortableHeader
                              label={t(`${DC}.letters.columns.lastUpdated`)}
                              field="updatedAt"
                              columnSort={columnSort}
                              onToggle={toggleColumnSort}
                            />
                          </th>
                          <th>{t(`${DC}.letters.columns.preview`)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagination.items.map((letter) => {
                          const isChecked = selectedIds.has(letter.id);
                          const rowClass = [
                            selectedId === letter.id ? 'selected' : '',
                            isChecked ? 'row-checked' : '',
                          ]
                            .filter(Boolean)
                            .join(' ');
                          return (
                          <tr
                            key={letter.id}
                            className={rowClass}
                            onClick={() => setSelectedId(letter.id)}
                            data-testid={`letter-row-${letter.id}`}
                          >
                            <td className="dc-table-col-check">
                              <input
                                type="checkbox"
                                className="dc-checkbox"
                                checked={isChecked}
                                onChange={(e) => toggleRowSelected(e, letter.id)}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Select ${letter.description}`}
                              />
                            </td>
                            <td>{letter.description}</td>
                            <td>{letter.bureau}</td>
                            <td className="dc-col-recipient">
                              <TruncateCell value={letter.recipient} />
                            </td>
                            <td className="dc-col-address">
                              <TruncateCell value={letter.address} />
                            </td>
                            <td>
                              <span className={statusClass(letter.lifecycleStatus)}>
                                {statusLabel(letter.lifecycleStatus)}
                              </span>
                            </td>
                            <td>{formatLetterDate(letter.createdAt)}</td>
                            <td>{formatLetterDate(letter.updatedAt)}</td>
                            <td>
                              <button
                                type="button"
                                className="dc-btn dc-btn-outline dc-btn-small"
                                onClick={(e) => handlePreview(e, letter)}
                              >
                                <Eye size={14} />
                                {t(`${DC}.letters.columns.preview`)}
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="dc-pagination">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <label htmlFor="dc-per-page" style={{ fontSize: 13, color: 'var(--dc-muted)' }}>
                        {t(`${DC}.pagination.rowsPerPage`)}
                      </label>
                      <select
                        id="dc-per-page"
                        className="dc-select"
                        value={perPage}
                        onChange={(e) => setPerPage(Number(e.target.value))}
                      >
                        {PER_PAGE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <span style={{ fontSize: 13, color: 'var(--dc-muted)' }}>
                        {t(`${DC}.pagination.range`, {
                          start: pagination.start,
                          end: pagination.end,
                          total: pagination.total,
                        })}
                      </span>
                    </div>
                    <div className="dc-actions">
                      <button
                        type="button"
                        className="dc-btn dc-btn-outline dc-btn-small"
                        disabled={pagination.page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        {t(`${DC}.pagination.prev`)}
                      </button>
                      <span className="dc-pill">{t(`${DC}.pagination.page`, { page: pagination.page })}</span>
                      <button
                        type="button"
                        className="dc-btn dc-btn-outline dc-btn-small"
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                      >
                        {t(`${DC}.pagination.next`)}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* 9. Bureau Outcome Matrix */}
        <section className="dc-card">
          <div className="dc-card-inner">
            <div className="dc-section-head">
              <div>
                <h2 className="dc-section-title">{t(`${DC}.outcomes.title`)}</h2>
                <p className="dc-section-copy">{t(`${DC}.outcomes.subtitle`)}</p>
              </div>
            </div>
            <div className="dc-table-wrap dc-outcome-matrix">
              <table className="dc-table">
                <thead>
                  <tr>
                    <th />
                    <th>Equifax</th>
                    <th>Experian</th>
                    <th>TransUnion</th>
                  </tr>
                </thead>
                <tbody>
                  {outcomeMatrix.map((row) => (
                    <tr key={row.key}>
                      <td style={{ fontWeight: 700 }}>{t(`${DC}.outcomes.rows.${row.key}`)}</td>
                      {row.counts.map((count, i) => (
                        <td key={i}>{count}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* 10. Latest Activity */}
        <section className="dc-card">
          <div className="dc-card-inner">
            <div className="dc-section-head">
              <div>
                <h2 className="dc-section-title">{t(`${DC}.latestActivity.title`)}</h2>
                <p className="dc-section-copy">{t(`${DC}.latestActivity.subtitle`)}</p>
              </div>
            </div>
            {latestEvents.length === 0 ? (
              <p className="dc-section-copy">{t(`${DC}.latestActivity.empty`)}</p>
            ) : (
              <div className="dc-event-list">
                {latestEvents.map((ev) => (
                  <div key={ev.id} className="dc-event-card">
                    <div className="dc-event-top">
                      <div>
                        <div className="dc-event-title">{ev.bureau}</div>
                        <div className="dc-event-copy">{t(`${DC}.activity.${ev.titleKey}`)}</div>
                      </div>
                      <div className="dc-event-date">{ev.dateLabel}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <ChooseLetterPreviewModal
        isOpen={choosePreviewOpen}
        onClose={() => {
          setChoosePreviewOpen(false);
          setChoosePreviewLetters([]);
          setChoosePreviewDefaultId(null);
        }}
        letters={choosePreviewLetters}
        defaultLetterId={choosePreviewDefaultId}
        onSelect={handleChoosePreviewLetter}
        statusLabel={statusLabel}
        title={t(`${DC}.preview.chooseTitle`)}
        subtitle={t(`${DC}.preview.chooseSubtitle`)}
      />

      <LetterPreviewModal
        isOpen={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewLetter(null);
          setLetterContent(null);
          setResolvedAttachments([]);
          setPdfApiAttachments([]);
        }}
        letterContent={letterContent?.content ?? letterContent?.html ?? letterContent}
        packageInfo={previewLetter?.rawPackage}
        packages={previewLetter ? [previewLetter.rawPackage] : []}
        currentPackageIndex={0}
        onDownload={handleDownloadLetter}
        onPostGridPreview={handlePostGridPreview}
        onSendLetter={previewCanSend ? handleFutureSendLetter : undefined}
        canSendLetter={previewCanSend}
        isLoading={letterLoading}
        resolvedAttachments={resolvedAttachments}
        supportingDocumentsTitle={t(`${DC}.preview.supportingDocumentsTitle`)}
        noSupportingDocumentsLabel={t(`${DC}.preview.noSupportingDocuments`)}
        postGridPreviewLabel={t(`${DC}.preview.postGridPdf`)}
        sendLetterLabel={t(`${DC}.preview.sendLetter`)}
      />

      {postgridPreviewOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(6,28,20,0.85)] backdrop-blur-sm p-4"
          data-testid="dispute-center-postgrid-preview-modal"
        >
          <div className="relative w-full max-w-5xl h-[90vh] bg-stone-900 rounded-2xl border border-slate-700 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
              <h3 className="text-white font-semibold">{t(`${DC}.preview.postGridPdf`)}</h3>
              <button
                type="button"
                className="dc-btn dc-btn-outline dc-btn-small"
                onClick={() => {
                  setPostgridPreviewOpen(false);
                  cleanupPostgridPreviewUrl();
                }}
                data-testid="dispute-center-postgrid-preview-close"
              >
                {t(`${DC}.preview.close`)}
              </button>
            </div>
            <div className="flex-1 bg-stone-950">
              {postgridPreviewLoading ? (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  {t(`${DC}.preview.postGridLoading`)}
                </div>
              ) : postgridPreviewUrl ? (
                <iframe
                  title={t(`${DC}.preview.postGridPdf`)}
                  src={postgridPreviewUrl}
                  className="w-full h-full border-0"
                  data-testid="dispute-center-postgrid-preview-iframe"
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeCenterDashboard;
